import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert, Box, Button, Card, Link, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import ScheduleService, {
  DAY_LABEL, SCHEDULE_DAYS, ScheduleDay, ScheduleDriver, ScheduleOverview, WorkSchedule,
} from '../../api/services/schedule.service';
import { describeError } from '../../api/services/payroll.service';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import ToneChip from '../../components/ui/ToneChip';
import { LoadingState } from '../../components/ui/States';
import { useAuthStore } from '../../store/authStore';
import DayCell from './DayCell';
import SchedulePanel from './SchedulePanel';
import { toggleDay } from './scheduleMath';

// The usual week: what each person can carry and the days they work. Every
// week follows this unless that week was changed by hand. Which WA each
// person runs is set on Work areas; the WA column here only shows it.
//
// Who is on it is not kept here: it is every current Driver and BC, from the
// company's people, so a new hire appears and someone who left drops off by
// themselves. Somebody new comes with a starting point from the statements
// (their usual days, their busiest day) and a "New" marker until saved.

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' } as const;
const smallInput = { '& input': { py: 0.5, px: 0.75, textAlign: 'right' } };

const toRow = (d: ScheduleDriver): ScheduleDriver => ({
  accountId: d.accountId,
  name: d.name,
  role: d.role,
  saved: d.saved,
  workArea: d.workArea || '',
  maxStops: d.maxStops,
  maxPackages: d.maxPackages,
  days: d.days,
});

/** Today's roster: what is saved, and a starting point for anyone without it. */
const rowsFrom = ({ plan, suggestions, defaultGrowthPercent }: ScheduleOverview): WorkSchedule => {
  const suggested = new Map(suggestions.drivers.map((d) => [d.accountId, d]));
  const drivers = (plan ? plan.drivers : suggestions.drivers).map((d) => (
    d.saved ? toRow(d) : toRow({ ...d, ...suggested.get(d.accountId), saved: false })
  ));
  return {
    drivers,
    workAreas: plan ? plan.workAreas : suggestions.workAreas,
    growthPercent: plan ? plan.growthPercent : defaultGrowthPercent,
    defaultMaxStops: plan?.defaultMaxStops ?? null,
    defaultMaxPackages: plan?.defaultMaxPackages ?? null,
  };
};

const UsualWeek: React.FC = () => {
  const { canArea } = useAuthStore();
  const editable = canArea('operations', 'edit');
  const [overview, setOverview] = useState<ScheduleOverview | null>(null);
  const [plan, setPlan] = useState<WorkSchedule | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    ScheduleService.overview()
      .then((body) => { setOverview(body); setPlan(rowsFrom(body)); })
      .catch((err) => setError(describeError(err, 'Could not load the schedule.')));
  }, []);

  const change = (next: WorkSchedule) => { setPlan(next); setDirty(true); setSaved(false); };
  const setDriver = (id: string, patch: Partial<ScheduleDriver>) => {
    if (plan) change({ ...plan, drivers: plan.drivers.map((d) => (d.accountId === id ? { ...d, ...patch } : d)) });
  };

  const save = async () => {
    if (!plan) return;
    setSaving(true);
    try {
      const body = await ScheduleService.savePlan(plan);
      setPlan({ ...rowsFrom({ ...overview!, plan: body.plan }), drivers: body.plan.drivers.map(toRow) });
      setOverview((o) => (o ? { ...o, plan: body.plan } : o));
      setDirty(false);
      setSaved(true);
      setError(null);
    } catch (err) {
      setError(describeError(err, 'The schedule was not saved.'));
    } finally {
      setSaving(false);
    }
  };

  const fromData = overview && !overview.plan;
  const newcomers = useMemo(() => plan?.drivers.filter((d) => !d.saved).length ?? 0, [plan]);
  const on = (day: ScheduleDay) => plan?.drivers.filter((d) => d.days.includes(day)).length ?? 0;
  const dayCols = SCHEDULE_DAYS.map((d) => <col key={d} style={{ width: 62 }} />);

  return (
    <PageBody fluid>
      <SchedulePanel />
      <PageHeader
        title="Usual week"
        subtitle="Every week follows this, except the changes made on that week."
        actions={editable && (
          <Button variant="contained" size="small" disabled={saving || (!dirty && !fromData && !newcomers)} onClick={save}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        )}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(false)}>Saved.</Alert>}
      {fromData ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This draft comes from your recent statements. Check each person&apos;s days and limits, then save. Who runs which WA is on <Link component={RouterLink} to="/schedule/work-areas">Work areas</Link>.
        </Alert>
      ) : newcomers > 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {newcomers === 1 ? 'One person is' : `${newcomers} people are`} new to the schedule. Their days and limits are a starting point, from the statements or the default capacity. Check them, then save.
        </Alert>
      )}

      {!plan ? (error ? null : <LoadingState />) : (
        <Stack gap={2.5}>
          <Card sx={{ overflow: 'hidden' }}>
            <Stack direction="row" alignItems="center" gap={2} sx={{ p: 1.5 }} flexWrap="wrap">
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle2">Drivers and BCs</Typography>
                <Typography variant="caption" color="text.secondary">Everyone current in Users, kept up to date for you.</Typography>
              </Box>
              <Tooltip title="Expected volume is the same week last year raised by this much. A week can use its own.">
                <TextField
                  size="small" type="number" label="Expected %" value={plan.growthPercent} disabled={!editable} sx={{ width: 110 }}
                  onChange={(e) => change({ ...plan, growthPercent: Number(e.target.value) })}
                />
              </Tooltip>
            </Stack>
            <TableContainer>
              <Table size="small" sx={{ minWidth: 860, tableLayout: 'fixed' }}>
                <colgroup>
                  <col />
                  <col style={{ width: 76 }} />
                  <col style={{ width: 76 }} />
                  <col style={{ width: 84 }} />
                  {dayCols}
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ px: 1.25 }}>Name</TableCell>
                    <TableCell align="right" sx={{ px: 1 }}><Tooltip title="The WA they usually run, set on Work areas. Blank is a floater."><span>WA</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ px: 1 }}><Tooltip title="Most stops in a day"><span>Stops</span></Tooltip></TableCell>
                    <TableCell align="right" sx={{ px: 1 }}><Tooltip title="Most packages in a day"><span>Pkgs</span></Tooltip></TableCell>
                    {SCHEDULE_DAYS.map((d) => <TableCell key={d} align="center" sx={{ px: 0.5 }}>{DAY_LABEL[d]}</TableCell>)}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.drivers.map((d) => (
                    <TableRow key={d.accountId} hover>
                      <TableCell sx={{ py: 0.25, px: 1.25 }}>
                        <Stack direction="row" alignItems="center" gap={0.75} sx={{ minWidth: 0 }}>
                          <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</Box>
                          {d.role === 'BC' && <Typography component="span" variant="caption" color="text.secondary">BC</Typography>}
                          {!d.saved && !fromData && <ToneChip tone="info" label="New" />}
                          {d.training && (
                            <Tooltip title="In training: rides with their trainer, so they add no capacity until training is finished.">
                              <span><ToneChip tone="warn" label="Training" /></span>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align="right" sx={{ ...num, py: 0.25, px: 1, fontWeight: 700 }}>
                        {plan.workAreas.find((w) => w.driverId === d.accountId)?.number || d.workArea || (
                          <Typography component="span" variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 0.25, px: 0.5 }}>
                        <TextField
                          size="small" type="number" value={d.maxStops} disabled={!editable} sx={smallInput}
                          inputProps={{ 'aria-label': `${d.name} max stops`, min: 0 }}
                          onChange={(e) => setDriver(d.accountId, { maxStops: Math.max(0, Math.round(Number(e.target.value))) })}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.25, px: 0.5 }}>
                        <TextField
                          size="small" type="number" value={d.maxPackages} disabled={!editable} sx={smallInput}
                          inputProps={{ 'aria-label': `${d.name} max packages`, min: 0 }}
                          onChange={(e) => setDriver(d.accountId, { maxPackages: Math.max(0, Math.round(Number(e.target.value))) })}
                        />
                      </TableCell>
                      {SCHEDULE_DAYS.map((day) => (
                        <TableCell key={day} sx={{ py: 0.25, px: 0.5 }}>
                          <DayCell
                            on={d.days.includes(day)} label={`${d.name}, ${DAY_LABEL[day]}`} disabled={!editable}
                            onToggle={() => setDriver(d.accountId, { days: toggleDay(d.days, day) })}
                          />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} align="right" sx={{ px: 1.25, fontWeight: 700 }}>Scheduled drivers</TableCell>
                    {SCHEDULE_DAYS.map((d) => <TableCell key={d} align="center" sx={{ ...num, fontWeight: 700, px: 0.5 }}>{on(d)}</TableCell>)}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>

          <Typography variant="caption" color="text.secondary">
            The work areas FedEx dispatches each day, and who runs them, are on <Link component={RouterLink} to="/schedule/work-areas">Work areas</Link>.
          </Typography>
        </Stack>
      )}
    </PageBody>
  );
};

export default UsualWeek;
